import { Head } from 'components/Head';
import { useIntl } from 'react-intl';

import NewButton from 'components/NewButton';
import PageHeader from 'components/PageHeader';
import { useAuth } from 'hooks/context/useAuthContext';
import { getPermission, ADMIN, DELETE } from 'apis/permissions';
import { useSorting } from 'hooks/useSorting';

import { TransactionTypesSearchResult } from 'features/transaction-types/components/TransactionTypesSearchResult';
import { TransactionTypesSearchBox } from 'features/transaction-types/components/TransactionTypesSearchBox';
import { Loader } from '@entur/loader';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Suspense } from 'react';
import { useTransactionTypesSearch } from 'features/transaction-types/hooks/useChartOfAccountsSearch';
import {
  getTransactionTypesQueryOptions,
  TransactionTypesSearchParamsType,
} from 'features/transaction-types/api/getTransactionTypes';
import { useQueryClient } from '@tanstack/react-query';

const TransactionTypesRoute = () => {
  const intl = useIntl();
  const { hasPrivilege } = useAuth();
  const queryClient = useQueryClient();

  const { searchParams, defaultSearchParams, setSearchParams } = useTransactionTypesSearch();
  const { handleSorting, getSortDirection } = useSorting(searchParams, setSearchParams);

  const handleSearch = (data: TransactionTypesSearchParamsType) => {
    queryClient.invalidateQueries({ queryKey: getTransactionTypesQueryOptions(data).queryKey });
    setSearchParams({ ...data, page: 0 });
  };

  return (
    <>
      <Head title={intl.formatMessage({ id: 'transactionTypes.namePlural' })} />
      <PageHeader
        heading={intl.formatMessage({ id: 'transactionTypes.namePlural' })}
        rightGroup={
          hasPrivilege(getPermission(ADMIN, DELETE)) ? (
            <NewButton text={intl.formatMessage({ id: 'transactionTypes.create' })} link="/transaction-types/new" />
          ) : null
        }
      />
      <TransactionTypesSearchBox
        defaultSearchParams={defaultSearchParams}
        searchParams={searchParams}
        handleSearch={handleSearch}
      />
      <ErrorBoundary>
        <Suspense fallback={<Loader>{intl.formatMessage({ id: 'generalValues.loading' })}</Loader>}>
          <TransactionTypesSearchResult
            searchParams={searchParams}
            sortConfig={{ handleSorting, getSortDirection }}
            handlePaging={(page, size) => setSearchParams({ page, size })}
          />
        </Suspense>
      </ErrorBoundary>
    </>
  );
};

export default TransactionTypesRoute;
